import {
	Alert,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableFooter,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Fragment, type KeyboardEvent, useId, useRef } from "react";
import { type Control, Controller } from "react-hook-form";

import type { SectionSpecItem } from "@/shared/models/SectionSpec";
import {
	formatMeasurement,
	formatValidMeasurement,
	validateMeasurement,
} from "@/shared/utils/measurementPrecision";
import { statusOf } from "@/pages/products/test-shack/models/Inspection";
import type { InspectionFormValues } from "@/pages/products/test-shack/models/InspectionForm";
import {
	formatTolerance,
	isDimensionEditable,
	readingKey,
} from "@/pages/products/test-shack/utils/inspectionFormValues";
import { useLegDeltas } from "../../hooks/useLegDeltas";

interface DimensionReadingsTableProps {
	dimensions: SectionSpecItem[];
	control: Control<InspectionFormValues>;
	isEditingInspection: boolean;
	/** Shows the Δ legs footer; omit when the deltas are shown elsewhere. */
	legDeltaFooter?: { max: number | null } | null;
	compact?: boolean;
}

interface ToleranceCellProps {
	value: number | null;
}

interface LegDeltaFooterRowProps {
	label: string;
	value: number | null;
	max: number | null;
	isOutOfTolerance: boolean;
}

const DIMENSION_TABLE_COLUMN_WIDTHS = {
	dimension: "12%",
	min: "22%",
	target: "22%",
	entered: "22%",
	max: "22%",
} as const;
const DIMENSION_TABLE_HEADINGS = [
	{ label: "Min", width: DIMENSION_TABLE_COLUMN_WIDTHS.min, align: "center" },
	{
		label: "Target",
		width: DIMENSION_TABLE_COLUMN_WIDTHS.target,
		align: "center",
	},
	{
		label: "Entered",
		width: DIMENSION_TABLE_COLUMN_WIDTHS.entered,
		align: "center",
	},
	{ label: "Max", width: DIMENSION_TABLE_COLUMN_WIDTHS.max, align: "center" },
] as const;
const ENTERED_INPUT_HEIGHT = 28;

const ToleranceCell = ({ value }: ToleranceCellProps) => (
	<TableCell
		align="center"
		sx={{
			py: 0.25,
			px: 1,
			color: "inherit",
			fontFamily: "monospace",
			fontWeight: 400,
			fontVariantNumeric: "tabular-nums",
		}}
	>
		{formatTolerance(value)}
	</TableCell>
);

const LegDeltaFooterRow = ({
	label,
	value,
	max,
	isOutOfTolerance,
}: LegDeltaFooterRowProps) => (
	<TableRow>
		<TableCell
			colSpan={3}
			sx={{ py: 0.5, px: 1, color: "text.secondary", fontWeight: 500 }}
		>
			{label}
		</TableCell>
		<TableCell
			align="center"
			sx={{
				py: 0.5,
				px: 1,
				color: isOutOfTolerance ? "error.main" : "text.primary",
				fontFamily: "monospace",
				fontVariantNumeric: "tabular-nums",
				fontWeight: 500,
			}}
		>
			{value !== null ? formatMeasurement(value) : "-"}
		</TableCell>
		<TableCell
			align="center"
			sx={{
				py: 0.5,
				px: 1,
				color: "text.secondary",
				fontFamily: "monospace",
				fontVariantNumeric: "tabular-nums",
			}}
		>
			{max !== null ? `≤ ${formatMeasurement(max)}` : "-"}
		</TableCell>
	</TableRow>
);

const LegDeltaFooter = ({
	control,
	dimensions,
	max,
}: {
	control: Control<InspectionFormValues>;
	dimensions: SectionSpecItem[];
	max: number | null;
}) => {
	const { topDelta, bottomDelta, topOut, bottomOut } = useLegDeltas(
		control,
		dimensions,
		max,
	);
	return (
		<TableFooter sx={{ backgroundColor: "action.hover" }}>
			<LegDeltaFooterRow
				label="Δ top legs"
				value={topDelta}
				max={max}
				isOutOfTolerance={topOut}
			/>
			<LegDeltaFooterRow
				label="Δ bot. legs"
				value={bottomDelta}
				max={max}
				isOutOfTolerance={bottomOut}
			/>
		</TableFooter>
	);
};

export const DimensionReadingsTable = ({
	dimensions,
	control,
	isEditingInspection,
	legDeltaFooter,
	compact = false,
}: DimensionReadingsTableProps) => {
	const measurementId = useId();
	const enteredInputRefs = useRef(new Map<number, HTMLInputElement>());
	const editableDimensions = dimensions.filter((dimension) =>
		isDimensionEditable(dimension, isEditingInspection),
	);
	const applicableDimensions = dimensions.filter(
		(dimension) => dimension.Applies,
	);
	const hasNoLimits =
		applicableDimensions.length > 0 &&
		applicableDimensions.every(
			(dimension) =>
				dimension.Ltl === null &&
				dimension.Nominal === null &&
				dimension.Utl === null,
		);

	const handleEnteredKeyDown = (
		event: KeyboardEvent<HTMLDivElement>,
		dimensionId: number,
	) => {
		if (
			(event.key !== "Enter" &&
				event.key !== "ArrowUp" &&
				event.key !== "ArrowDown") ||
			event.altKey ||
			event.ctrlKey ||
			event.metaKey ||
			event.shiftKey ||
			event.nativeEvent.isComposing
		)
			return;

		event.preventDefault();
		const currentIndex = editableDimensions.findIndex(
			(dimension) => dimension.ProfileItemId === dimensionId,
		);
		const direction = event.key === "ArrowUp" ? -1 : 1;
		const nextDimension = editableDimensions[currentIndex + direction];
		if (currentIndex < 0 || !nextDimension) return;

		enteredInputRefs.current.get(nextDimension.ProfileItemId)?.focus();
	};

	return (
		<Stack spacing={1} sx={{ width: "100%", minWidth: 0 }}>
			{hasNoLimits && (
				<Alert severity="warning" variant="outlined">
					This section has no published tolerances yet. Readings are recorded
					without limit checks.
				</Alert>
			)}
			<TableContainer
				sx={{
					border: 1,
					borderColor: "divider",
					borderRadius: 1,
					width: "100%",
					minWidth: 0,
				}}
			>
				<Table
					size="small"
					aria-label="Sample dimensions"
					sx={{
						tableLayout: "fixed",
						"& .MuiTableCell-root": compact
							? { py: 0.125, fontSize: "12px" }
							: undefined,
					}}
				>
					<TableHead sx={{ backgroundColor: "action.hover" }}>
						<TableRow>
							<TableCell
								sx={{
									py: 0.5,
									px: 1,
									width: DIMENSION_TABLE_COLUMN_WIDTHS.dimension,
									color: "text.secondary",
									fontWeight: 500,
								}}
							>
								Dim
							</TableCell>
							{DIMENSION_TABLE_HEADINGS.map((column) => (
								<TableCell
									key={column.label}
									align={column.align}
									sx={{
										py: 0.5,
										px: 1,
										width: column.width,
										color: "text.secondary",
										fontWeight: 500,
									}}
								>
									{column.label}
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{dimensions.map((dimension) => {
							const isEditable = isDimensionEditable(
								dimension,
								isEditingInspection,
							);
							const errorId = `${measurementId}-${dimension.ProfileItemId}-error`;

							return (
								<Controller
									key={dimension.ProfileItemId}
									name={`readings.${readingKey(dimension.ProfileItemId)}`}
									control={control}
									defaultValue=""
									rules={{
										deps: "readings",
										validate: (raw) =>
											!isEditable || validateMeasurement(raw).message || true,
									}}
									render={({ field }) => {
										const raw = dimension.Applies ? (field.value ?? "") : "";
										const validation = validateMeasurement(raw);
										const status = statusOf(
											validation.value,
											dimension.Ltl,
											dimension.Utl,
										);
										const isOutOfTolerance =
											status === "Over" || status === "Under";
										const validationMessage = isEditable
											? validation.message
											: null;

										return (
											<Fragment>
												<TableRow
													hover={dimension.Applies}
													aria-disabled={!dimension.Applies}
													sx={{
														color: dimension.Applies
															? "text.primary"
															: "text.disabled",
														"&& > *": { color: "inherit" },
													}}
												>
													<TableCell sx={{ py: 0.25, px: 1 }}>
														<Typography
															component="span"
															variant="body2"
															color="inherit"
															sx={{
																fontWeight: 400,
																fontSize: compact ? "12px" : undefined,
															}}
														>
															{dimension.Code}
														</Typography>
													</TableCell>
													<ToleranceCell
														value={dimension.Applies ? dimension.Ltl : null}
													/>
													<ToleranceCell
														value={dimension.Applies ? dimension.Nominal : null}
													/>
													<TableCell align="center" sx={{ py: 0.25, px: 0.5 }}>
														<TextField
															{...field}
															id={`inspection-reading-${dimension.ProfileItemId}`}
															variant="outlined"
															margin="none"
															size="small"
															value={raw}
															onBlur={() => {
																const formattedValue =
																	formatValidMeasurement(raw);
																if (formattedValue !== raw)
																	field.onChange(formattedValue);
																field.onBlur();
															}}
															onKeyDown={(event) =>
																handleEnteredKeyDown(
																	event,
																	dimension.ProfileItemId,
																)
															}
															inputRef={(input: HTMLInputElement | null) => {
																field.ref(input);
																if (input) {
																	enteredInputRefs.current.set(
																		dimension.ProfileItemId,
																		input,
																	);
																} else {
																	enteredInputRefs.current.delete(
																		dimension.ProfileItemId,
																	);
																}
															}}
															disabled={!isEditable}
															error={
																validationMessage !== null || isOutOfTolerance
															}
															fullWidth
															sx={{
																"& .MuiInputBase-root.Mui-error": {
																	backgroundColor: (theme) =>
																		alpha(
																			theme.palette.error.main,
																			theme.palette.action.selectedOpacity,
																		),
																},
															}}
															slotProps={{
																htmlInput: {
																	"data-test-id": `inspection-reading-${dimension.ProfileItemId}`,
																	"aria-label": `Entered ${dimension.Code}`,
																	"aria-describedby": validationMessage
																		? errorId
																		: undefined,
																	inputMode: "decimal",
																},
																input: {
																	slotProps: {
																		root: {
																			...{
																				"data-test-id": `inspection-reading-field-${dimension.ProfileItemId}`,
																			},
																		},
																		...{
																			notchedOutline: {
																				"data-test-id": `inspection-reading-outline-${dimension.ProfileItemId}`,
																			},
																		},
																	},
																	sx: {
																		height: compact ? 24 : ENTERED_INPUT_HEIGHT,
																		fontFamily: "monospace",
																		fontSize: compact ? "12px" : undefined,
																		fontWeight: 500,
																		fontVariantNumeric: "tabular-nums",
																		"& input": {
																			py: 0.5,
																			px: 1,
																			textAlign: "center",
																		},
																	},
																},
															}}
														/>
													</TableCell>
													<ToleranceCell
														value={dimension.Applies ? dimension.Utl : null}
													/>
												</TableRow>
												{validationMessage && (
													<TableRow data-measurement-error={dimension.Code}>
														<TableCell
															colSpan={5}
															id={errorId}
															sx={{
																color: "error.main",
																overflowWrap: "anywhere",
															}}
														>
															{validationMessage}
														</TableCell>
													</TableRow>
												)}
											</Fragment>
										);
									}}
								/>
							);
						})}
					</TableBody>
					{legDeltaFooter && (
						<LegDeltaFooter
							control={control}
							dimensions={dimensions}
							max={legDeltaFooter.max}
						/>
					)}
				</Table>
			</TableContainer>
		</Stack>
	);
};
